/**
 * Per-site/scenario Test-Fit Studio camera memory.
 *
 * Cameras are operator view preferences only. They never enter the frozen
 * generation-layout input or any auditable engineering snapshot.
 */
import {
  isValidBlueprintCamera,
  isValidStudioMapCamera,
  type StudioViewportSnapshot,
} from './studioCamera';

const KEY_PREFIX = 'siterag:testFitStudioViewport:v2:';

export function readStudioViewportMemory(key: string): StudioViewportSnapshot | null {
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StudioViewportSnapshot>;
    const blueprint = isValidBlueprintCamera(parsed.blueprint)
      ? parsed.blueprint
      : undefined;
    const map = isValidStudioMapCamera(parsed.map) ? parsed.map : undefined;
    return blueprint || map ? { blueprint, map } : null;
  } catch {
    return null;
  }
}

export function writeStudioViewportMemory(
  key: string,
  viewport: StudioViewportSnapshot,
): void {
  if (!key || typeof window === 'undefined') return;
  const blueprint = isValidBlueprintCamera(viewport.blueprint)
    ? viewport.blueprint
    : undefined;
  const map = isValidStudioMapCamera(viewport.map) ? viewport.map : undefined;
  try {
    if (!blueprint && !map) {
      window.localStorage.removeItem(KEY_PREFIX + key);
      return;
    }
    window.localStorage.setItem(
      KEY_PREFIX + key,
      JSON.stringify({ blueprint, map }),
    );
  } catch {
    /* View memory is optional (private mode / quota / disabled storage). */
  }
}

export function clearStudioViewportMemory(key: string): void {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY_PREFIX + key);
  } catch {
    /* optional preference */
  }
}

/** Test hook — clear every Test-Fit Studio viewport. */
export function clearAllStudioViewportMemoryForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(KEY_PREFIX)) window.localStorage.removeItem(key);
    }
  } catch {
    /* optional preference */
  }
}
